import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { marketAPI, signalsAPI } from '../services/api'
import type {
  PortfolioHeat,
  RiskStatus,
  SignalPerformanceSummary,
  SparklineSeries,
  TradingSignal,
} from '../types'
import toast from 'react-hot-toast'
import LoadingScreen from '../components/LoadingScreen'
import PortfolioHeatGauge from '../components/PortfolioHeatGauge'
import Spinner from '../components/Spinner'
import Tooltip from '../components/Tooltip'
import TradeTicketModal from '../components/trading/TradeTicketModal'
import SignalDetailCard from '../components/signals/SignalDetailCard'
import SignalRow from '../components/signals/SignalRow'
import ResultsDisclaimer from '../components/ResultsDisclaimer'
import EngineStatusRibbon from '../components/signals/EngineStatusRibbon'
import { isOpenSignal } from '../components/signals/signalChips'
import useKrakenAccess from '../hooks/useKrakenAccess'
import useLockACall from '../hooks/useLockACall'
import { MY_CALLS_PATH } from '../routes'
import useEscapeKey from '../hooks/useEscapeKey'
import { SIGNALS_GLOSSARY as GLOSSARY } from '../config/glossary'
import { formatHeatPct } from '../utils/portfolioHeat'
import { parseApiDate } from '../utils/signalExpiry'
import { displaySymbol } from '../utils/displaySymbol'

// Matches the sparkline endpoint's server-side cache TTL; the latest close is
// the live price the rows and the card mark P&L against.
const SPARKLINE_POLL_MS = 60_000
// Expiry countdowns show minutes at their finest.
const EXPIRY_TICK_MS = 60_000

// Open signals are few (portfolio heat caps them), so one generous page holds them all.
const OPEN_SIGNAL_LIMIT = 100
// Closed signals pile up; they load a page at a time behind "Load more".
const CLOSED_PAGE_SIZE = 20

// Below this the expanded card opens as a slide-over sheet rather than a sticky
// column, so it never swallows a phone screen. Matches Tailwind's `md`.
const DESKTOP_MIN_WIDTH = 768

// Past this scroll offset the pinned desktop card condenses (exit plan and
// leverage collapse away); back above it, they re-expand.
const CONDENSE_AT = 96

const newestFirst = (a: TradingSignal, b: TradingSignal) =>
  parseApiDate(b.created_at).getTime() - parseApiDate(a.created_at).getTime()

type SignalView = 'active' | 'closed'

type TradingMode = 'conservative' | 'growth'
type ModeFilter = 'all' | TradingMode

const MODE_FILTERS: { key: ModeFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'conservative', label: 'Conservative' },
  { key: 'growth', label: 'Growth' },
]

const modeOf = (signal: TradingSignal): TradingMode => (signal.trading_mode === 'growth' ? 'growth' : 'conservative')

/**
 * Signals: the record only. The engine console lives on Settings → Engine now;
 * here a slim status ribbon says the scanner is alive, and everything else is
 * the read-only record — the mode filter, Active/Closed tabs, one expanded card
 * over a list of compact rows, and the model-performance panel. "Trade this
 * signal" appears only for users who pass the live-trading gate.
 */
const Signals = () => {
  // The default view is open signals only. Closed ones live on their own tab,
  // fetched lazily and paged, so losers from weeks ago never bury live setups.
  // Both lists ask for real signals only (demo=false): demo-validation runs
  // open 5m, 50%-confidence plumbing tests that are not trade ideas.
  const [view, setView] = useState<SignalView>('active')
  const [signals, setSignals] = useState<TradingSignal[]>([])
  const [closedSignals, setClosedSignals] = useState<TradingSignal[]>([])
  const [closedSkip, setClosedSkip] = useState(0)
  const [closedHasMore, setClosedHasMore] = useState(false)
  const [closedLoaded, setClosedLoaded] = useState(false)
  const [closedLoading, setClosedLoading] = useState(false)
  // Toggling cancelled restarts the closed list; this drops a stale page that
  // lands after the restart.
  const closedRequest = useRef(0)
  // Cancelled signals (never scored) are hidden unless asked for.
  const [showCancelled, setShowCancelled] = useState(false)
  const [selected, setSelected] = useState<Record<SignalView, number | null>>({ active: null, closed: null })
  const [modeFilter, setModeFilter] = useState<ModeFilter>('all')
  const [loading, setLoading] = useState(true)
  const [riskStatus, setRiskStatus] = useState<RiskStatus | null>(null)
  const [portfolioHeat, setPortfolioHeat] = useState<PortfolioHeat | null>(null)
  const [perfStats, setPerfStats] = useState<SignalPerformanceSummary | null>(null)
  const [perfOpen, setPerfOpen] = useState(false)
  const [perfPlatform, setPerfPlatform] = useState('twitter')
  const [generatingPost, setGeneratingPost] = useState(false)
  const [generatedPost, setGeneratedPost] = useState('')
  const [sparklines, setSparklines] = useState<Record<string, SparklineSeries>>({})
  const [ticketSignal, setTicketSignal] = useState<number | null>(null)
  // On a phone the tapped signal opens as a slide-over instead of a sticky card.
  const [sheetOpen, setSheetOpen] = useState(false)
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window === 'undefined' ? true : window.innerWidth >= DESKTOP_MIN_WIDTH
  )
  // Desktop only: the pinned card condenses once the list scrolls past it.
  const [condensed, setCondensed] = useState(false)
  // Live trading and the engine controls share one gate: /api/kraken/access.
  const canTrade = useKrakenAccess()
  const canLockCalls = Boolean(useLockACall())
  // One clock for every countdown, rather than a timer per row.
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), EXPIRY_TICK_MS)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= DESKTOP_MIN_WIDTH)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Toggle the condensed state from the page scroll (not CSS scroll-timeline),
  // rAF-throttled. Only meaningful while the sticky desktop card is shown.
  useEffect(() => {
    if (!isDesktop) {
      setCondensed(false)
      return
    }
    let raf = 0
    const onScroll = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        setCondensed(window.scrollY > CONDENSE_AT)
      })
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [isDesktop])

  useEffect(() => {
    loadSignals()
    loadRiskStatus()
    loadPortfolioHeat()
    loadPerfStats()
  }, [])

  // A stable key, so the poll restarts only when the set of open symbols
  // changes rather than on every signals update.
  const openSymbolsKey = useMemo(
    () => Array.from(new Set(signals.filter(isOpenSignal).map((s) => s.symbol))).sort().join(','),
    [signals]
  )

  useEffect(() => {
    if (!openSymbolsKey) return
    let cancelled = false

    const load = async () => {
      try {
        const data = await marketAPI.getSparklines(openSymbolsKey.split(','))
        if (!cancelled) {
          setSparklines(Object.fromEntries(data.sparklines.map((s) => [s.symbol, s])))
        }
      } catch {
        // Display only: rows and the card show "—" for P&L without a price.
      }
    }

    load()
    const timer = setInterval(load, SPARKLINE_POLL_MS)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [openSymbolsKey])

  const loadSignals = async () => {
    try {
      const [active, pending] = await Promise.all([
        signalsAPI.list({ status: 'active', demo: false, limit: OPEN_SIGNAL_LIMIT }),
        signalsAPI.list({ status: 'pending', demo: false, limit: OPEN_SIGNAL_LIMIT }),
      ])
      setSignals(mergeOpen([...active, ...pending]))
    } catch {
      toast.error('Failed to load signals')
    } finally {
      setLoading(false)
    }
  }

  // Takes the flag rather than reading showCancelled, so a toggle can load the
  // list it is switching to before that state has updated.
  const loadClosedSignals = async (includeCancelled: boolean, skip: number) => {
    const request = ++closedRequest.current
    setClosedLoading(true)
    try {
      // One extra row says whether another page exists.
      const page = await signalsAPI.list({
        ...(includeCancelled ? { include_cancelled: true } : { status: 'closed' }),
        demo: false,
        skip,
        limit: CLOSED_PAGE_SIZE + 1,
      })
      if (request !== closedRequest.current) return
      const rows = page.slice(0, CLOSED_PAGE_SIZE)
      // Including cancelled means no status filter, so open signals come back
      // too; they belong on the Active tab.
      const resolved = rows.filter((signal) => !isOpenSignal(signal))
      setClosedSignals((prev) => (skip === 0 ? resolved : [...prev, ...resolved]))
      setClosedSkip(skip + rows.length)
      setClosedHasMore(page.length > CLOSED_PAGE_SIZE)
      setClosedLoaded(true)
    } catch {
      if (request === closedRequest.current) toast.error('Failed to load closed signals')
    } finally {
      if (request === closedRequest.current) setClosedLoading(false)
    }
  }

  const showView = (next: SignalView) => {
    setView(next)
    setSheetOpen(false)
    if (next === 'closed' && !closedLoaded && !closedLoading) loadClosedSignals(showCancelled, 0)
  }

  const loadRiskStatus = async () => {
    try {
      const status = await signalsAPI.riskStatus()
      setRiskStatus(status)
    } catch {
      // non-critical
    }
  }

  const loadPortfolioHeat = async () => {
    try {
      const heat = await signalsAPI.portfolioHeat()
      setPortfolioHeat(heat)
    } catch {
      // non-critical
    }
  }

  const loadPerfStats = async () => {
    try {
      const data = await signalsAPI.performanceSummary(90)
      setPerfStats(data)
    } catch {
      // non-critical
    }
  }

  const handleGeneratePost = async () => {
    setGeneratingPost(true)
    setGeneratedPost('')
    try {
      const result = await signalsAPI.generatePerformanceContent(perfPlatform, 90)
      setGeneratedPost(result.generated_text)
    } catch (error: any) {
      const detail = error.response?.data?.detail
      if (typeof detail === 'string' && detail.includes('fewer than')) {
        toast.error('Need at least 5 closed signals to generate a calibration post.')
      } else {
        toast.error(detail || 'Content generation failed')
      }
    } finally {
      setGeneratingPost(false)
    }
  }

  const selectSignal = (id: number) => {
    setSelected((s) => ({ ...s, [view]: id }))
    // On a phone the tap opens the slide-over; on desktop the sticky card just
    // swaps and this flag is ignored (the sheet is desktop-hidden).
    setSheetOpen(true)
  }

  useEscapeKey(() => setSheetOpen(false), sheetOpen && !isDesktop)

  if (loading) return <LoadingScreen />

  const byMode = (list: TradingSignal[]) => (modeFilter === 'all' ? list : list.filter((s) => modeOf(s) === modeFilter))
  const list = byMode(view === 'active' ? signals : closedSignals)
  // Default to the most recently opened; lists are newest first.
  const current = list.find((s) => s.id === selected[view]) ?? list[0] ?? null
  const priceOf = (s: TradingSignal) => (isOpenSignal(s) ? sparklines[s.symbol]?.last_price ?? null : null)
  const listLoading = view === 'closed' && !closedLoaded

  const renderCard = (isCondensed: boolean) =>
    current && (
      <SignalDetailCard
        key={current.id}
        signal={current}
        price={priceOf(current)}
        now={now}
        onTrade={canTrade ? () => setTicketSignal(current.id) : undefined}
        condensed={isCondensed}
        permalink
      />
    )

  return (
    <div className="space-y-4">
      {/* Header: title + the one filter everyone gets */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <h1 className="text-3xl font-bold text-gray-900">Trading Signals</h1>
          {/* My calls opens from here, not the sidebar: Lock a call shows only
              when the server says it's on for this user (LOCK_A_CALL_ENABLED,
              superusers only for now). */}
          {canLockCalls && (
            <Link to={MY_CALLS_PATH} className="text-sm font-semibold text-primary-400 hover:text-primary-300">
              My calls →
            </Link>
          )}
        </div>
        <div className="flex items-center gap-2 self-start">
          <div role="group" aria-label="Trading mode" className="flex items-center gap-1 rounded-lg border border-terminal-border bg-terminal-surface p-1">
            {MODE_FILTERS.map((m) => (
              <button
                key={m.key}
                type="button"
                aria-pressed={modeFilter === m.key}
                onClick={() => setModeFilter(m.key)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  modeFilter === m.key
                    ? 'bg-accent text-terminal-bg'
                    : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <Tooltip content={GLOSSARY.tradingMode} label="risk mode" />
        </div>
      </div>

      {/* Risk / drawdown guardrail banner */}
      {riskStatus && (riskStatus.daily_limit_hit || riskStatus.drawdown_warning) && (
        <div className="bg-danger-50 border border-danger-200 rounded-lg px-4 py-3 text-sm text-danger-800 flex items-start gap-2">
          <span className="text-danger-500 mt-0.5">⚠</span>
          <div>
            <strong>Drawdown alert</strong> — {riskStatus.message}
          </div>
        </div>
      )}

      {/* Where the engine console used to be: a display-only heartbeat. */}
      <EngineStatusRibbon isAdmin={canTrade} />

      {/* Portfolio heat: one slim strip */}
      {portfolioHeat && portfolioHeat.active_count > 0 && (
        <div
          className={`flex flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-lg border px-3 py-1.5 text-xs ${
            portfolioHeat.warning ? 'bg-danger-50 border-danger-200' : 'bg-white border-gray-200'
          }`}
          data-testid="heat-strip"
        >
          <span className={portfolioHeat.warning ? 'font-semibold text-danger-700' : 'text-gray-500'}>
            <Tooltip content={GLOSSARY.portfolioHeat(formatHeatPct(portfolioHeat.max_heat_pct, 0))}>Portfolio heat</Tooltip>
            {' · '}
            {portfolioHeat.active_count} active{portfolioHeat.warning ? ' — reduce exposure before adding more' : ''}
          </span>
          <PortfolioHeatGauge variant="inline" heatPct={portfolioHeat.total_heat_pct} maxHeatPct={portfolioHeat.max_heat_pct} />
        </div>
      )}

      {/* Active / Closed */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="tablist" aria-label="Signal status" className="flex items-center gap-1 rounded-lg border border-terminal-border bg-terminal-surface p-1">
          {(['active', 'closed'] as SignalView[]).map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              id={`signals-tab-${tab}`}
              aria-selected={view === tab}
              aria-controls="signals-panel"
              onClick={() => showView(tab)}
              className={`inline-flex items-center gap-2 rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
                view === tab ? 'bg-accent text-terminal-bg' : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
              }`}
            >
              {tab === 'active' ? 'Active' : 'Closed'}
              {tab === 'active' && <span className="text-xs tabular-nums opacity-80">{byMode(signals).length}</span>}
            </button>
          ))}
        </div>
        {view === 'closed' && (
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-gray-400">
            <input
              type="checkbox"
              checked={showCancelled}
              onChange={() => {
                const next = !showCancelled
                setShowCancelled(next)
                loadClosedSignals(next, 0)
              }}
              className="rounded border-gray-300"
            />
            Show cancelled
          </label>
        )}
      </div>

      <div role="tabpanel" id="signals-panel" aria-labelledby={`signals-tab-${view}`} className="space-y-3">
        {view === 'closed' && (
          <p className="text-xs text-gray-400" data-testid="closed-note">
            Expired signals close at the market price and count toward the record.
          </p>
        )}

        {listLoading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : list.length === 0 ? (
          <div className="rounded-lg border border-terminal-border bg-terminal-surface p-8 text-center sm:p-12">
            <p className="text-gray-400">
              {view === 'active'
                ? modeFilter === 'all'
                  ? 'No active signals right now.'
                  : `No active ${modeFilter} signals right now.`
                : showCancelled
                ? 'No closed or cancelled signals yet.'
                : 'No closed signals yet.'}
            </p>
            {view === 'active' && (
              <button type="button" onClick={() => showView('closed')} className="mt-3 text-sm font-medium text-accent hover:underline">
                View closed signals
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop: the expanded card is pinned at the top of the column at
                full height while the rows scroll underneath it. */}
            {isDesktop && current && (
              <div className="sticky top-4 z-20">{renderCard(condensed)}</div>
            )}
            <ul className="space-y-2" aria-label={view === 'active' ? 'Active signals' : 'Closed signals'}>
              {list.map((signal) => (
                <SignalRow
                  key={signal.id}
                  signal={signal}
                  price={priceOf(signal)}
                  now={now}
                  selected={current?.id === signal.id}
                  onSelect={() => selectSignal(signal.id)}
                />
              ))}
            </ul>
            <ResultsDisclaimer />
            {view === 'closed' && (
              <div className="flex flex-col items-center gap-2">
                {closedHasMore && (
                  <button
                    type="button"
                    onClick={() => loadClosedSignals(showCancelled, closedSkip)}
                    disabled={closedLoading}
                    className="flex w-full items-center justify-center gap-2 rounded-md border border-terminal-border bg-terminal-surface px-6 py-2 text-sm font-medium text-gray-300 hover:bg-white/5 disabled:opacity-50 sm:w-auto"
                  >
                    {closedLoading && <Spinner size="sm" className="border-gray-400 border-t-transparent" />}
                    {closedLoading ? 'Loading…' : 'Load more'}
                  </button>
                )}
                <p className="text-xs text-gray-500">
                  Showing {closedSignals.length} most recent{closedHasMore ? '' : ' (all)'}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Mobile: the tapped signal opens as a slide-over over the list, so the
          full-height card never swallows the screen and the list keeps its
          scroll position underneath. */}
      {!isDesktop && sheetOpen && current && (
        <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true" aria-label={`${displaySymbol(current.symbol)} signal`}>
          <div className="absolute inset-0 bg-black/70" onClick={() => setSheetOpen(false)} aria-hidden="true" />
          <div className="absolute inset-x-0 bottom-0 top-14 overflow-y-auto rounded-t-2xl border-t border-terminal-border bg-terminal-bg p-4 shadow-2xl">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-gray-500">Signal detail</span>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="rounded-md border border-terminal-border px-3 py-1 text-sm text-gray-200 hover:bg-white/5"
                data-testid="close-sheet"
              >
                Close
              </button>
            </div>
            {renderCard(false)}
          </div>
        </div>
      )}

      {/* The record: model performance, public */}
      {perfStats && perfStats.total_signals > 0 && (
        <div className="bg-white rounded-lg shadow border border-gray-200">
          <button
            className="w-full flex items-center justify-between px-4 py-3 text-left"
            onClick={() => setPerfOpen((o) => !o)}
          >
            <span className="font-semibold text-gray-800">
              Model Performance
              <span className="ml-2 text-sm font-normal text-gray-500">
                {perfStats.total_signals} closed signals
                {perfStats.overall_win_rate !== null && ` · ${perfStats.overall_win_rate.toFixed(1)}% overall WR`}
              </span>
            </span>
            <span className="text-gray-400 text-sm">{perfOpen ? '▲' : '▼'}</span>
          </button>

          {perfOpen && (
            <div className="border-t border-gray-100 px-4 py-4 space-y-5">
              {/* By regime — backend sends win_rate as 0-100 (e.g. 65.3) */}
              {Object.keys(perfStats.by_regime).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Win Rate by Regime</p>
                  <div className="space-y-2">
                    {Object.entries(perfStats.by_regime).map(([regime, s]) => (
                      <div key={regime} className="flex items-center gap-3">
                        <span className="w-24 text-sm text-gray-600 capitalize shrink-0">{regime}</span>
                        <div className="flex-1 bg-gray-100 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${s.win_rate >= 55 ? 'bg-green-500' : s.win_rate >= 45 ? 'bg-yellow-400' : 'bg-red-400'}`}
                            style={{ width: `${Math.min(s.win_rate, 100)}%` }}
                          />
                        </div>
                        <span className="w-20 text-right text-sm tabular-nums text-gray-700">
                          {s.win_rate.toFixed(1)}% <span className="text-gray-400">({s.total})</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* By confidence bucket */}
              {Object.keys(perfStats.by_confidence_bucket).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Win Rate by Confidence Bucket</p>
                  <div className="space-y-2">
                    {Object.entries(perfStats.by_confidence_bucket)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([bucket, s]) => (
                        <div key={bucket} className="flex items-center gap-3">
                          <span className="w-24 text-sm text-gray-600 shrink-0">{bucket}</span>
                          <div className="flex-1 bg-gray-100 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${s.win_rate >= 55 ? 'bg-green-500' : s.win_rate >= 45 ? 'bg-yellow-400' : 'bg-red-400'}`}
                              style={{ width: `${Math.min(s.win_rate, 100)}%` }}
                            />
                          </div>
                          <span className="w-20 text-right text-sm tabular-nums text-gray-700">
                            {s.win_rate.toFixed(1)}% <span className="text-gray-400">({s.total})</span>
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* By leverage tier */}
              {perfStats.by_leverage_tier && Object.keys(perfStats.by_leverage_tier).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Win Rate by Leverage Tier</p>
                  <div className="space-y-2">
                    {(['blocked', 'conservative', 'moderate', 'aggressive'] as const)
                      .filter(k => perfStats.by_leverage_tier![k])
                      .map(k => {
                        const s = perfStats.by_leverage_tier![k]
                        return (
                          <div key={k} className="flex items-center gap-3">
                            <span className="w-36 text-sm text-gray-600 shrink-0">{s.label}</span>
                            <div className="flex-1 bg-gray-100 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${s.win_rate >= 55 ? 'bg-green-500' : s.win_rate >= 45 ? 'bg-yellow-400' : 'bg-red-400'}`}
                                style={{ width: `${Math.min(s.win_rate, 100)}%` }}
                              />
                            </div>
                            <span className="w-28 text-right text-sm tabular-nums text-gray-700">
                              {s.win_rate.toFixed(1)}%{' '}
                              <span className={`text-xs ${s.avg_pnl_pct >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                                ({s.avg_pnl_pct >= 0 ? '+' : ''}{s.avg_pnl_pct.toFixed(1)}%)
                              </span>{' '}
                              <span className="text-gray-400">({s.total})</span>
                            </span>
                          </div>
                        )
                      })}
                  </div>
                </div>
              )}

              {/* Generate calibration post: a creator tool, admins only */}
              {canTrade && (
              <div className="border-t border-gray-100 pt-4 space-y-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Generate Calibration Post</p>
                <div className="flex items-center gap-3 flex-wrap">
                  <select
                    value={perfPlatform}
                    onChange={(e) => { setPerfPlatform(e.target.value); setGeneratedPost('') }}
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    {['twitter', 'instagram', 'linkedin', 'tiktok', 'blog'].map((p) => (
                      <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                    ))}
                  </select>
                  <button
                    onClick={handleGeneratePost}
                    disabled={generatingPost}
                    className="px-4 py-1.5 bg-accent text-terminal-bg text-sm font-semibold rounded-md hover:bg-accent-hover disabled:opacity-50 flex items-center gap-2"
                  >
                    {generatingPost && <Spinner size="sm" className="border-terminal-bg border-t-transparent" />}
                    {generatingPost ? 'Generating…' : 'Generate Post'}
                  </button>
                </div>
                {generatedPost && (
                  <div className="space-y-1">
                    <textarea
                      readOnly
                      value={generatedPost}
                      rows={8}
                      className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 bg-gray-50 resize-y focus:outline-none"
                    />
                    <button
                      onClick={() => { navigator.clipboard.writeText(generatedPost); toast.success('Copied!') }}
                      className="text-xs text-accent hover:underline"
                    >
                      Copy to clipboard
                    </button>
                  </div>
                )}
              </div>
              )}
            </div>
          )}
        </div>
      )}
      {ticketSignal != null && <TradeTicketModal signalId={ticketSignal} onClose={() => setTicketSignal(null)} />}
    </div>
  )
}

/**
 * Adds open, real signals to the active list: newest first, no duplicates
 * (active + pending can overlap). Anything closed or demo is left out, matching
 * what the list endpoint is asked for.
 */
const mergeOpen = (incoming: TradingSignal[]) => {
  const byId = new Map<number, TradingSignal>()
  for (const signal of incoming) {
    if (isOpenSignal(signal) && !signal.is_demo && !byId.has(signal.id)) byId.set(signal.id, signal)
  }
  return Array.from(byId.values()).sort(newestFirst)
}

export default Signals
